import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './app/auth/useAuth';
import { TelaAfericao } from './app/afericao/TelaAfericao';
import { HomeScreen } from './app/components/HomeScreen';
import { LoadingEmpresaScreen } from './app/components/LoadingEmpresaScreen';
import { PermissionRequestScreen } from './app/components/PermissionRequestScreen';
import { ForaDaAreaScreen } from './app/components/ForaDaAreaScreen';
import { BootstrapDebug } from './app/components/BootstrapDebug';
import { bootstrapFlow } from './app/bootstrap/bootstrapFlow';
import { iniciarAfericao } from './app/afericao/iniciarAfericao';
import { getAfericaoPorId } from './app/offline/api';
import { checkGeolocationPermission } from './app/utils/permissions';
import { useEffect, useState, useRef } from 'react';

function CallbackHandler() {
  const { handleAuthCallback } = useAuth();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Evitar processar múltiplas vezes
    if (hasProcessed.current || isProcessing) {
      console.log('[Router] Callback já foi processado ou está em processamento, ignorando...');
      return;
    }

    hasProcessed.current = true;
    setIsProcessing(true);
    console.log('[Router] Processando callback OAuth...');
    
    handleAuthCallback()
      .then((user) => {
        console.log('[Router] Callback processado com sucesso, redirecionando...');
        // Limpar parâmetros da URL antes de redirecionar
        window.history.replaceState({}, document.title, '/');
        // Redirecionar para a página principal após login bem-sucedido
        window.location.href = '/';
      })
      .catch((err) => {
        console.error('[Router] Erro no callback:', err);
        console.error('[Router] Stack trace:', err.stack);
        
        // Se o erro for relacionado a estado OAuth perdido ou código já usado,
        // limpar storage e redirecionar para login
        const errorMessage = err.message || err.error || '';
        if (errorMessage.includes('No matching state') || 
            errorMessage.includes('already been redeemed') ||
            errorMessage.includes('invalid_grant')) {
          console.log('[Router] Erro de estado OAuth, limpando storage e redirecionando para login...');
          // Limpar storage do OIDC
          localStorage.removeItem(`oidc.user:${import.meta.env.VITE_AUTH_URL || 'https://localhost:5001'}:${import.meta.env.VITE_CLIENT_ID || 'afericoes'}`);
          // Limpar parâmetros da URL
          window.history.replaceState({}, document.title, '/');
          // Redirecionar para login após um pequeno delay
          setTimeout(() => {
            window.location.href = '/';
          }, 1000);
          return;
        }
        
        setError(err.message || 'Erro ao processar autenticação');
        setIsProcessing(false);
      });
  }, []); // Remover handleAuthCallback da dependência para evitar múltiplas execuções

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Erro na Autenticação</h2>
        <p>{error}</p>
        <button onClick={() => (window.location.href = '/')}>Voltar</button>
      </div>
    );
  }

  return <LoadingEmpresaScreen />;
}

export function Router() {
  console.log('📍 [Router] Componente Router sendo renderizado');
  console.log('%c[Router] ====== ROUTER RENDERIZADO ======', 'background: #009900; color: white; font-size: 14px; padding: 4px;');
  
  console.log('[Router] Chamando useAuth()...');
  const { isAuthenticated, isLoading, login, user } = useAuth();
  console.log('[Router] useAuth retornou:', { isAuthenticated, isLoading });
  const location = useLocation();
  const navigate = useNavigate();

  // Detectar logout=success e limpar cookies
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('logout') === 'success') {
      console.log('[Router] Detectado logout=success, removendo cookies e limpando storage...');
      
      // Remover cookie de autenticação do servidor
      const cookieNames = [
        '.AspNetCore.Cookies',
        '.AspNetCore.Identity.Application',
        'idsrv',
        'idsrv.session',
        'OpenIdConnect.nonce'
      ];
      
      const domain = window.location.hostname;
      const domainParts = domain.split('.');
      const domainsToTry = [domain];
      
      if (domainParts.length > 1) {
        domainsToTry.push('.' + domainParts.slice(-2).join('.'));
        domainsToTry.push('.' + domainParts.slice(-1).join('.'));
      }
      
      domainsToTry.forEach(dom => {
        cookieNames.forEach(cookieName => {
          const paths = ['/', '/connect', '/account', '/signin'];
          paths.forEach(path => {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${dom};`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${dom}; SameSite=Lax;`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${dom}; SameSite=Strict;`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${dom}; SameSite=None; Secure;`;
          });
        });
      });
      
      // Limpar tokens do localStorage/sessionStorage
      localStorage.removeItem('auth_token');
      sessionStorage.clear();
      
      // Limpar dados do OIDC
      const oidcStorageKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('oidc.user:') || key.startsWith('oidc.')
      );
      oidcStorageKeys.forEach(key => localStorage.removeItem(key));
      
      console.log('[Router] Cookies e storage limpos. Removendo parâmetro logout=success da URL...');
      
      // Remover o parâmetro logout=success da URL
      const newSearch = new URLSearchParams(location.search);
      newSearch.delete('logout');
      const newUrl = location.pathname + (newSearch.toString() ? `?${newSearch.toString()}` : '');
      navigate(newUrl, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);
  const [bootstrapState, setBootstrapState] = useState({
    loading: false,
    error: null,
    empresa: null,
    afericao: null,
    foraDaArea: false,
    obtendoGeolocalizacao: false,
    solicitandoPermissaoLocalizacao: false,
    usuarioClicouSolicitar: false // Flag para garantir que usuário clicou em "Solicitar"
  });
  const [currentAfericaoId, setCurrentAfericaoId] = useState(null);
  const bootstrapExecutingRef = useRef(false);

  console.log('[Router] Estado inicial:', {
    isAuthenticated,
    isLoading,
    pathname: location.pathname,
    bootstrapLoading: bootstrapState.loading
  });

  // Tratar callback OAuth
  if (location.pathname === '/callback') {
    return <CallbackHandler />;
  }

  // Redirecionar para login se não autenticado
  useEffect(() => {
    console.log('[Router] useEffect de autenticação executado:', {
      isLoading,
      isAuthenticated,
      pathname: location.pathname
    });
    
    // Não redirecionar para login se o bootstrap já está carregando
    // Isso evita interromper o fluxo quando o useAuth tem timeout mas o usuário está autenticado
    if (!isLoading && !isAuthenticated && location.pathname !== '/callback' && !bootstrapState.loading) {
      console.log('[Router] Não autenticado, redirecionando para login...');
      // Usar setTimeout para evitar atualização durante render
      const timeoutId = setTimeout(() => {
        try {
          login();
        } catch (error) {
          console.error('[Router] Erro ao chamar login():', error);
        }
      }, 0);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, isAuthenticated, location.pathname, login, bootstrapState.loading]);

  // Executar bootstrap quando autenticado
  useEffect(() => {
    let isMounted = true;
    
    if (
      isAuthenticated && 
      !isLoading && 
      location.pathname !== '/callback' && 
      !bootstrapState.empresa && // Só mostrar tela de permissão se não houver empresa ainda
      !bootstrapState.foraDaArea && // Não executar se já está fora da área
      !bootstrapState.solicitandoPermissaoLocalizacao && // Evitar mostrar novamente se já está sendo solicitado
      !bootstrapState.usuarioClicouSolicitar && // Evitar mostrar se usuário já clicou em "Solicitar"
      !bootstrapState.loading && // Evitar mostrar se já está carregando
      !bootstrapState.error // Não executar se há erro
    ) {
      console.log('[Router] Autenticado, mas sem empresa - verificando permissão de localização...');
      
      // Verificar se a permissão já foi concedida
      checkGeolocationPermission().then((hasPermission) => {
        if (!isMounted) return;
        
        if (hasPermission) {
          console.log('[Router] Permissão de localização já concedida, iniciando bootstrap diretamente...');
          // Se já tem permissão, marcar que usuário clicou em solicitar
          // O segundo useEffect vai executar o bootstrap
          setBootstrapState((prev) => {
            // Se já está executando, não fazer nada
            if (bootstrapExecutingRef.current) {
              return prev;
            }
            return {
              ...prev,
              loading: false, // Não setar loading aqui, deixar o segundo useEffect fazer isso
              usuarioClicouSolicitar: true
            };
          });
        } else {
          console.log('[Router] Permissão de localização não concedida, mostrando tela de permissão...');
          // Se não tem permissão, mostrar tela de permissão
          setBootstrapState((prev) => ({ 
            ...prev, 
            loading: false,
            solicitandoPermissaoLocalizacao: true 
          }));
        }
      }).catch((error) => {
        console.error('[Router] Erro ao verificar permissão de localização:', error);
        // Em caso de erro, mostrar tela de permissão por segurança
        if (isMounted) {
          setBootstrapState((prev) => ({ 
            ...prev, 
            loading: false,
            solicitandoPermissaoLocalizacao: true 
          }));
        }
      });
      
      return () => {
        isMounted = false;
      };
    }
  }, [isAuthenticated, isLoading, location.pathname, bootstrapState.empresa, bootstrapState.foraDaArea, bootstrapState.solicitandoPermissaoLocalizacao, bootstrapState.usuarioClicouSolicitar, bootstrapState.loading, bootstrapState.error]);

  // Executar bootstrap após usuário solicitar permissão de localização
  useEffect(() => {
    // Se usuarioClicouSolicitar é true e não há empresa ainda, precisamos iniciar o bootstrap
    const shouldStartBootstrap = bootstrapState.usuarioClicouSolicitar && !bootstrapState.empresa;
    
    console.log('[Router] Verificando se deve iniciar bootstrap:', {
      shouldStartBootstrap,
      usuarioClicouSolicitar: bootstrapState.usuarioClicouSolicitar,
      empresa: bootstrapState.empresa,
      isAuthenticated,
      isLoading,
      solicitandoPermissaoLocalizacao: bootstrapState.solicitandoPermissaoLocalizacao,
      error: bootstrapState.error,
      bootstrapExecutingRef: bootstrapExecutingRef.current
    });
    
    if (
      isAuthenticated && 
      !isLoading && 
      location.pathname !== '/callback' &&
      !bootstrapState.solicitandoPermissaoLocalizacao &&
      shouldStartBootstrap &&
      bootstrapState.error === null &&
      !bootstrapExecutingRef.current // Evitar múltiplas execuções
    ) {
      console.log('[Router] Iniciando bootstrap...');
      bootstrapExecutingRef.current = true;
      setBootstrapState((prev) => ({ ...prev, loading: true }));
      
      const timeoutId = setTimeout(() => {
        console.error('[Router] Timeout no bootstrap após 60 segundos');
        setBootstrapState({
          loading: false,
          error: 'Timeout: O carregamento está demorando muito. Verifique sua conexão e tente novamente.',
          empresa: null,
          afericao: null,
          foraDaArea: false,
          solicitandoPermissaoLocalizacao: false,
          usuarioClicouSolicitar: false
        });
        bootstrapExecutingRef.current = false;
      }, 60000);
      
      bootstrapFlow((isGettingLocation) => {
        setBootstrapState((prev) => ({ ...prev, obtendoGeolocalizacao: isGettingLocation }));
      })
        .then((result) => {
          clearTimeout(timeoutId);
          setBootstrapState({
            loading: false,
            error: null,
            empresa: result.empresa,
            afericao: result.afericao,
            foraDaArea: result.foraDaArea === true,
            obtendoGeolocalizacao: false,
            solicitandoPermissaoLocalizacao: false,
            usuarioClicouSolicitar: false
          });
          bootstrapExecutingRef.current = false;
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          console.error('[Router] Erro no bootstrap:', error);
          setBootstrapState({
            loading: false,
            error: error.message || 'Erro desconhecido no bootstrap',
            empresa: null,
            afericao: null,
            foraDaArea: false,
            obtendoGeolocalizacao: false,
            solicitandoPermissaoLocalizacao: false,
            usuarioClicouSolicitar: false
          });
          bootstrapExecutingRef.current = false;
        });
    }
  }, [isAuthenticated, isLoading, location.pathname, bootstrapState.usuarioClicouSolicitar, bootstrapState.empresa, bootstrapState.solicitandoPermissaoLocalizacao, bootstrapState.error]);

  // Mostrar tela de permissão de localização antes de solicitar
  if (isAuthenticated && bootstrapState.solicitandoPermissaoLocalizacao) {
    return (
      <PermissionRequestScreen
        type="location"
        onRequest={() => {
          // Marcar que usuário clicou em "Solicitar" e remover flag de solicitação
          setBootstrapState((prev) => ({ 
            ...prev, 
            solicitandoPermissaoLocalizacao: false,
            usuarioClicouSolicitar: true
          }));
        }}
      />
    );
  }

  // Mostrar loading com modo apropriado
  if (isLoading) {
    return <LoadingEmpresaScreen mode="auth" />;
  }

  if (isAuthenticated && bootstrapState.loading) {
    return (
      <LoadingEmpresaScreen 
        mode={bootstrapState.obtendoGeolocalizacao ? 'location' : 'default'}
        obtendoGeolocalizacao={bootstrapState.obtendoGeolocalizacao}
      />
    );
  }

  if (!isAuthenticated) {
    return <LoadingEmpresaScreen mode="auth" />;
  }

  // Verificar se está fora da área ANTES de verificar se há empresa
  // Isso garante que a tela seja exibida mesmo quando não há empresa
  console.log('[Router] Verificando foraDaArea:', {
    foraDaArea: bootstrapState.foraDaArea,
    foraDaAreaType: typeof bootstrapState.foraDaArea,
    empresa: bootstrapState.empresa,
    loading: bootstrapState.loading,
    error: bootstrapState.error,
    bootstrapState: bootstrapState
  });
  
  if (bootstrapState.foraDaArea === true) {
    console.log('[Router] Exibindo tela ForaDaAreaScreen');
    return <ForaDaAreaScreen />;
  }

  if (bootstrapState.error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Erro</h2>
        <p>{bootstrapState.error}</p>
        <button onClick={() => window.location.reload()}>Tentar Novamente</button>
      </div>
    );
  }

  const handleIniciarAfericao = async () => {
    try {
      setBootstrapState((prev) => ({ ...prev, loading: true }));
      const afericaoId = await iniciarAfericao(bootstrapState.empresa);
      setCurrentAfericaoId(afericaoId);
      navigate('/afericao');
    } catch (error) {
      console.error('Erro ao iniciar aferição:', error);
      alert('Erro ao iniciar aferição. Tente novamente.');
    } finally {
      setBootstrapState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleContinuarAfericao = async (afericaoId) => {
    setCurrentAfericaoId(afericaoId);
    navigate('/afericao');
  };

  const handleAfericaoFinalizada = () => {
    // Voltar para a tela inicial e recarregar bootstrap
    setCurrentAfericaoId(null);
    navigate('/');
    
    // Recarregar dados
    if (isAuthenticated && !isLoading) {
      setBootstrapState((prev) => ({ ...prev, loading: true }));
      bootstrapFlow((isGettingLocation) => {
        setBootstrapState((prev) => ({ ...prev, obtendoGeolocalizacao: isGettingLocation }));
      })
        .then((result) => {
          setBootstrapState({
            loading: false,
            error: null,
            empresa: result.empresa,
            afericao: result.afericao,
            foraDaArea: result.foraDaArea || false,
            obtendoGeolocalizacao: false,
            solicitandoPermissaoLocalizacao: false,
            usuarioClicouSolicitar: false
          });
        })
        .catch((error) => {
          console.error('[Router] Erro ao recarregar bootstrap:', error);
          setBootstrapState((prev) => ({
            ...prev,
            loading: false,
            error: error.message || 'Erro ao recarregar dados',
            obtendoGeolocalizacao: false,
            solicitandoPermissaoLocalizacao: false,
            usuarioClicouSolicitar: false
          }));
        });
    }
  };

  if (bootstrapState.empresa) {
    return (
      <>
        <Routes>
          <Route
            path="/"
            element={
              <HomeScreen
                empresa={bootstrapState.empresa}
                afericaoAberta={bootstrapState.afericao}
                onIniciarAfericao={handleIniciarAfericao}
                onContinuarAfericao={handleContinuarAfericao}
                loading={bootstrapState.loading}
              />
            }
          />
          <Route
            path="/afericao"
            element={
              currentAfericaoId ? (
                <TelaAfericao
                  empresa={bootstrapState.empresa}
                  afericaoId={currentAfericaoId}
                  onFinalizada={handleAfericaoFinalizada}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {import.meta.env.DEV && (
          <BootstrapDebug
            bootstrapState={bootstrapState}
            authState={{ isLoading, isAuthenticated, user }}
          />
        )}
      </>
    );
  }

  // Se chegou aqui e não há empresa, mas também não está fora da área,
  // ainda está carregando ou houve algum erro
  // Mas se está fora da área, já deveria ter sido capturado acima
  // Este é um fallback de segurança
  if (bootstrapState.foraDaArea) {
    return <ForaDaAreaScreen />;
  }

  return (
    <>
      <LoadingEmpresaScreen />
      {import.meta.env.DEV && (
        <BootstrapDebug
          bootstrapState={bootstrapState}
          authState={{ isLoading, isAuthenticated, user }}
        />
      )}
    </>
  );
}

